import { ReactNode, useRef, Suspense } from "react";
import { useInView } from "@/hooks/useInView";

interface LazySectionProps {
  children: ReactNode;
  className?: string;
  minHeight?: string;
  rootMargin?: string;
}

/**
 * Lazy-mounts children when scrolled near the viewport.
 *
 * Anti-flicker rules (v2):
 * - Reserve `minHeight` BEFORE mount so layout is stable while scrolling.
 * - After mount we DO NOT pin a measured pixel height. Pinning caused
 *   re-layout flicker because the wrapper height changed 2–3 times
 *   (initial measure → ResizeObserver → setTimeout) as images/fonts
 *   settled, yanking the page below.
 * - Instead we keep `minHeight` as a floor — content can grow naturally
 *   above it without ever shrinking below it. This eliminates scroll jumps
 *   without re-flowing the page on every paint.
 */
const LazySection = ({ children, className, minHeight = "200px", rootMargin = "600px" }: LazySectionProps) => {
  const { ref, isInView } = useInView(rootMargin);
  const hasRendered = useRef(false);

  if (isInView) hasRendered.current = true;

  return (
    <div ref={ref} className={className} style={{ minHeight }}>
      {hasRendered.current ? (
        <Suspense fallback={<div style={{ minHeight }} />}>
          {children}
        </Suspense>
      ) : null}
    </div>
  );
};

export default LazySection;
